import {AsyncPipe
} from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, DOCUMENT } from '@angular/core';
import {DateAdapter} from '@angular/material/core';
import {Title} from '@angular/platform-browser';
import {
    Event,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    Router,
    RouterOutlet,
} from '@angular/router';
import {
    TranslocoEvents,
    TranslocoService,
} from '@jsverse/transloco';
import {
    UntilDestroy,
    untilDestroyed,
} from '@ngneat/until-destroy';
import {
    Actions,
} from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import {
    distinctUntilChanged,
    distinctUntilKeyChanged,
    filter,
    first,
    map,
    merge,
    skip,
    switchMap,
} from 'rxjs';
import {
    take,
    tap,
} from 'rxjs/operators';

import {
    BROADCAST_CHANNEL_AUTH_CHANGE_TOKEN,
    BROADCAST_CHANNEL_CONSENT_CHANGE_TOKEN,
    BROADCAST_REFRESH_SEARCH_UNIVERSES
} from '~/app/shared/constants/broadcast-channels.constants';
import {ENVESTBOARD_UNIVERSE_ID} from '~/app/domains/shares/constants/shares.constants';
import {ConsentProviderFacade} from '~/app/domains/consent/services/common/consent-provider.facade';
import {BroadcastChannelService} from '~/app/shared/services/broadcast-channel/broadcast-channel.service';
import {ConfigurationFacade} from '~/app/core/state/configuration/configuration.facade';
import {LocaleService} from '~/app/shared/services/locale/locale.service';
import {BaseTrackingService} from '~/app/shared/services/tracking/base-tracking.service';
import {AuthenticationTokens} from '~/app/domains/authentication/types/authentication-tokens.type';
import {BroadcastSearchUniverse} from '~/app/shared/types/broadcast/delete-search-universe.type';
import {ConsentState} from '~/app/domains/consent/types/consent-state.type';
import {User} from '~/app/shared/types/user/user.type';
import {environment} from '~/environments/environment';

import {ApplicationFacade} from './core/state/application/application-facade/application.facade';
import {AuthenticationFacade} from './core/state/authentication/authentication.facade';
import {PortfoliosListFacade} from './core/state/portfolios-list/portfolios-list.facade';
import {SharesListFacade} from './core/state/shares-list/shares-list.facade';
import {TurnstileComponent} from '~/app/domains/authentication/components/turnstile/turnstile.component';
import packageInfo from '../../package.json';
import {TranslocoHttpLoader} from '~/app/shared/services/transloco/transloco-loader';
import {SpinnerComponent} from '~/app/shared/components/elements/loaders/spinner/spinner.component';
import {PropositionFacade} from "~/app/domains/proposition/proposition-facade.service";
import {ModalDocumentComponent} from "~/app/shared/modals/modal-document/modal-document.component";
import {toSignal} from "@angular/core/rxjs-interop";
import {ModalService} from "~/app/shared/services/modal/modal.service";
import {VersionCheckService} from '~/app/core/services/version-check/version-check.service';

@UntilDestroy()
@Component({
    selector: 'eb-root',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./app.component.scss'],
    imports: [
        TurnstileComponent,
        RouterOutlet,
        AsyncPipe,
        SpinnerComponent,
    ]
})
export class AppComponent implements OnInit {
    private localeService = inject(LocaleService);
    private dateAdapter = inject<DateAdapter<any>>(DateAdapter);
    private router = inject(Router);
    private titleService = inject(Title);
    authenticationFacade = inject(AuthenticationFacade);
    private applicationFacade = inject(ApplicationFacade);
    private sharesListFacade = inject(SharesListFacade);
    private portfolioListFacade = inject(PortfoliosListFacade);
    private document = inject<Document>(DOCUMENT);
    private translocoHttpLoader = inject(TranslocoHttpLoader);
    private translocoService = inject(TranslocoService);
    private broadcastChannelService = inject(BroadcastChannelService);
    private actions$ = inject(Actions);
    private trackingService = inject(BaseTrackingService);
    private configurationFacade = inject(ConfigurationFacade);
    private propositionFacade = inject(PropositionFacade);
    private modalService = inject(ModalService);
    public versionCheckService = inject(VersionCheckService);

    public isLoading = true;

    public isChallengeRequired = this.configurationFacade.getChallengeRequiredSnapshot();
    public proposition = toSignal(this.propositionFacade.getPropositionDraft());

    public consentProviderFacade = inject(ConsentProviderFacade);
    protected readonly environment = environment;

    constructor() {

        if (this.configurationFacade.getIsMatomoTrackingEnabledSnapshot()) {
            this.trackingService.insertScript(this.configurationFacade.getTrackinMatomoSnapshot());
            this.updateMatomoUserId();
        }

        this.translocoService.events$
            .pipe(
                filter((e: TranslocoEvents) => e.type === 'translationLoadSuccess' || e.type === 'langChanged'),
                map((e) => e?.payload),
                distinctUntilChanged(((prev, curr) => prev.langName === curr.langName)),
                switchMap(({langName}) => this.translocoHttpLoader.getTranslationFromAPI(langName)),
                map((translation) => this.translocoService.setTranslation(translation)),
                untilDestroyed(this),
            ).subscribe();

        this.authenticationFacade.userLanguage$
            .pipe(
                take(1),
                tap((language) => void this.localeService.initLocale(language)),
                untilDestroyed(this),
            ).subscribe();

        this.localeService.currentDateFnsAdapterLocale$
            .pipe(untilDestroyed(this))
            .subscribe((lang) => {
                this.dateAdapter.setLocale(lang);
            });

        if (this.configurationFacade.getIsMatomoTrackingEnabledSnapshot()) {
            this.router.events
                .pipe(
                    filter((event: Event) => event instanceof NavigationEnd),
                    tap(() => this.trackingService.pageView()),
                    untilDestroyed(this),
                )
                .subscribe();
        }

        this.router.events
            .pipe(
                filter((event: Event) => event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError),
                first(),
                map(() => false),
                untilDestroyed(this),
            )
            .subscribe((isLoading: boolean) => {
                this.isLoading = isLoading;
            });

        this.titleService.setTitle(this.configurationFacade.getPlatformNameSnapshot());

        window.addEventListener('error', (event) => {
            if (this.isChunkLoadError(event.error ?? event.message)) {
                this.reloadOnChunkError();
            }
        });

        window.addEventListener('unhandledrejection', (event) => {
            if (this.isChunkLoadError(event.reason)) {
                this.reloadOnChunkError();
            }
        });

        this.versionCheckService.startPolling();
    }

    public ngOnInit(): void {

        console.info(`🚀 ${this.configurationFacade?.getPlatformNameSnapshot()} version: ${packageInfo?.version}`);

        if (!environment.production) {
            console.info(`Matomo status: ${this.configurationFacade.getIsMatomoTrackingEnabledSnapshot() ? 'enabled' : 'disabled'}`);
            console.info(`Sentry status: ${this.configurationFacade.getIsSentryTrackingEnabled() ? 'enabled' : 'disabled'}`);
        }
        if (this.configurationFacade.getIsSentryTrackingEnabled()) {
            Sentry.init({
                dsn: atob(this.configurationFacade?.getTrackingSentrySnapshot()?.dsn || ''),
                environment: this.configurationFacade?.getTrackingSentrySnapshot()?.extras?.env ?? undefined,
            });
        }

        // proposition finalize show document
        this.propositionFacade.onFinalisePropositionSucceed()
            .pipe(
                tap(() => {
                    const proposition = this.proposition();

                    if (proposition && proposition?.pdfDocument) {
                        this.modalService.open(ModalDocumentComponent, {link: proposition.pdfDocument?.url});
                    }
                }),
                untilDestroyed(this)
            ).subscribe();

        // Reset configuration after signin and signout
        merge(
            this.authenticationFacade.onLogin(),
            this.authenticationFacade.onLogout(),
        ).pipe(
            tap(() => {
                this.updateMatomoUserId();
            }),
            switchMap(() => this.configurationFacade.getConfiguration()),
            untilDestroyed(this),
        ).subscribe();

        // Check version on login — silent reload if new deploy detected
        this.authenticationFacade.onLogin().pipe(
            tap(() => this.versionCheckService.checkVersionAndReloadIfNeeded()),
            untilDestroyed(this),
        ).subscribe();

        this.broadcastChannelService.messagesOfType<AuthenticationTokens>(BROADCAST_CHANNEL_AUTH_CHANGE_TOKEN)
            .pipe(
                tap((message) => this.authenticationFacade.setAuthenticationTokens(message.payload)),
                untilDestroyed(this),
            )
            .subscribe();

        this.broadcastChannelService.messagesOfType<ConsentState>(BROADCAST_CHANNEL_CONSENT_CHANGE_TOKEN)
            .pipe(
                tap((message) => this.consentProviderFacade.updateIsConsentAccepted(message.payload.state)),
                untilDestroyed(this),
            )
            .subscribe();

        this.broadcastChannelService
            .messagesOfType<BroadcastSearchUniverse>(BROADCAST_REFRESH_SEARCH_UNIVERSES).pipe(
            tap(() => {
                this.sharesListFacade.changeSearchUniverse(ENVESTBOARD_UNIVERSE_ID);
                this.sharesListFacade.getSearchUniverses();
            }),
            untilDestroyed(this),
        )
            .subscribe();

        // Overwrite colors
        this.configurationFacade?.colorPalette$?.pipe(
            tap((colorPalette) => {
                if (colorPalette) {
                    Object.keys(colorPalette).forEach((key) => {
                        this.document.documentElement.style.setProperty(`--${key}`, colorPalette[key]);
                    });
                }
            }),
            untilDestroyed(this),
        ).subscribe();

        this.authenticationFacade.userLanguage$
            .pipe(
                skip(1),
                tap((language) => void this.localeService.setLocale(language)),
                distinctUntilChanged(),
                switchMap(() => this.authenticationFacade.user$.pipe(
                    filter((user): user is User => user !== null && user !== undefined && user.id !== undefined),
                    distinctUntilKeyChanged('id'),
                    tap((user) => {
                        if (user?.id) {
                            this.applicationFacade.initApplication();
                            this.sharesListFacade.resetState();
                            this.sharesListFacade.search(true);
                            this.portfolioListFacade.getPortfolioMetaData(true);
                            this.applicationFacade.getMenus();
                        }
                    }),
                )),
                untilDestroyed(this),
            ).subscribe();

    }

    public refreshChallengeToken(challenge: string) {
        this.authenticationFacade.refreshChallengeToken(challenge)
            .pipe(untilDestroyed(this))
            .subscribe();
    }

    private isChunkLoadError(error: unknown): boolean {
        if (error instanceof Error && error.name === 'ChunkLoadError') return true;

        const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
        return /chunk|dynamically imported module|Loading failed for the module/i.test(message);
    }

    private reloadOnChunkError(): void {
        const key = 'chunk_error_reload';
        const lastReload = sessionStorage.getItem(key);
        const now = Date.now();

        if (!lastReload || now - Number(lastReload) > 10_000) {
            sessionStorage.setItem(key, String(now));
            window.location.reload();
        }
    }

    private updateMatomoUserId() {
        const user = this.authenticationFacade.getUserSnapshot();
        const userId = user?.email || 'anonymous';
        this.trackingService.updateUserId(userId);
    }

}
